import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import type { CoverageWarning, Violation, ViolationRule, ViolationSeverity } from '@conciair/scheduler-core';
import { Button, Chip, EmptyState } from '../../ui/components';
import { Select, TextInput } from '../../ui/fields';
import { fmtDate } from '../../ui/format';
import { toCsv } from '../data/exportData';
import { parseDoubleSuggestions } from '../overrides/saveOverride';

const SEVERITIES: ViolationSeverity[] = ['HIGH', 'WARN', 'SOFT', 'INFO'];
const TONE: Record<ViolationSeverity, 'danger' | 'warn' | 'gold' | 'neutral'> = { HIGH: 'danger', WARN: 'warn', SOFT: 'gold', INFO: 'neutral' };
const RULE_LABEL: Record<ViolationRule, string> = {
  COVERAGE_GAP: 'Unfilled seat',
  COVERAGE_FORECAST: 'Coverage forecast',
  PAIRING_REPEAT: 'Same pairing 3+ times',
  TAIL_REPEAT: 'Same tail 3+ times',
  PHASE_SHIFT: 'Cadence break',
  SHORT_REST: 'Short rest (override)',
  FAIRNESS: 'Fairness deviation',
};

export interface FindingsProps {
  violations: Violation[];
  coverage?: CoverageWarning[];
  tails: string[];
  pilotNames: Map<string, string>;
  onShow?: (v: Violation) => void;
  onAddDouble?: (s: { pilotCode: string; tail: string; rotationStart: string }) => void;
  compact?: boolean;
}

/**
 * Findings (spec 5.3 drawer and 5.8 page): grouped by severity then rule,
 * plain-English explanations, "show on schedule", one-click DOUBLE
 * suggestions for coverage gaps, filters, CSV.
 */
export function FindingsList({ violations, coverage, tails, pilotNames, onShow, onAddDouble, compact = false }: FindingsProps) {
  const [sev, setSev] = useState<'' | ViolationSeverity>('');
  const [rule, setRule] = useState<'' | ViolationRule>('');
  const [tail, setTail] = useState('');
  const [pilot, setPilot] = useState('');

  const all = useMemo(() => {
    const extra: Violation[] = (coverage ?? [])
      .filter((c) => !violations.some((v) => v.rule === 'COVERAGE_FORECAST' && v.windowStart === c.startIso && v.subject === `${c.type} ${c.seat}`))
      .map((c) => ({ windowStart: c.startIso, windowEnd: c.endIso, subject: `${c.type} ${c.seat}`, rule: 'COVERAGE_FORECAST' as const, severity: 'WARN' as const, explanation: c.message }));
    return [...violations, ...extra];
  }, [violations, coverage]);

  const pilotOptions = useMemo(() => Array.from(pilotNames.entries()).filter(([code]) => all.some((v) => v.subject.includes(`(${code})`) || v.explanation.includes(`(${code})`))).sort((a, b) => a[1].localeCompare(b[1])), [pilotNames, all]);

  const visible = useMemo(
    () =>
      all
        .filter((v) => (sev ? v.severity === sev : true))
        .filter((v) => (rule ? v.rule === rule : true))
        .filter((v) => (tail ? v.subject.includes(tail) || v.explanation.includes(tail) : true))
        .filter((v) => (pilot ? v.subject.includes(`(${pilot})`) || v.explanation.includes(`(${pilot})`) : true))
        .sort((a, b) => SEVERITIES.indexOf(a.severity) - SEVERITIES.indexOf(b.severity) || a.rule.localeCompare(b.rule) || String(a.windowStart).localeCompare(String(b.windowStart))),
    [all, sev, rule, tail, pilot]
  );

  const groups = useMemo(() => {
    const g = new Map<string, Violation[]>();
    for (const v of visible) {
      const k = `${v.severity}|${v.rule}`;
      g.set(k, [...(g.get(k) ?? []), v]);
    }
    return Array.from(g.entries());
  }, [visible]);

  const counts = SEVERITIES.map((s) => [s, all.filter((v) => v.severity === s).length] as const);

  const exportCsv = () => {
    const body = visible.map((v) => [v.windowStart, v.windowEnd, v.subject, v.rule, v.severity, v.explanation]);
    const blob = new Blob([toCsv(['window_start', 'window_end', 'subject', 'rule', 'severity', 'explanation'], body)], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `findings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {counts.map(([s, n]) => (
          <button key={s} type="button" onClick={() => setSev(sev === s ? '' : s)} className={`rounded-full ${sev === s ? 'ring-2 ring-navy' : ''}`} title={`Show ${s} only`}>
            <Chip tone={TONE[s]}>{n} {s}</Chip>
          </button>
        ))}
        <div className="flex-1" />
        <Button onClick={exportCsv} className="text-xs"><Download size={12} /> CSV</Button>
      </div>
      {!compact && (
        <div className="flex flex-wrap items-center gap-2">
          <Select value={rule} onChange={(e) => setRule(e.target.value as '' | ViolationRule)} className="w-56">
            <option value="">All rules</option>
            {(Object.keys(RULE_LABEL) as ViolationRule[]).map((r) => <option key={r} value={r}>{RULE_LABEL[r]}</option>)}
          </Select>
          <Select value={tail} onChange={(e) => setTail(e.target.value)} className="w-32">
            <option value="">All tails</option>
            {tails.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
          <Select value={pilot} onChange={(e) => setPilot(e.target.value)} className="w-56">
            <option value="">All pilots</option>
            {pilotOptions.map(([code, name]) => <option key={code} value={code}>{name} ({code})</option>)}
          </Select>
          <TextInput value={tail} onChange={(e) => setTail(e.target.value)} placeholder="Search text" className="w-40" />
        </div>
      )}

      {visible.length === 0 && <EmptyState title={all.length === 0 ? 'No findings' : 'No findings match the filters'}>{all.length === 0 ? 'No unfilled seats, soft-rule breaches, or coverage warnings.' : ''}</EmptyState>}

      {groups.map(([key, items]) => {
        const [s, r] = key.split('|') as [ViolationSeverity, ViolationRule];
        return (
          <section key={key}>
            <h3 className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-ink-muted">
              <Chip tone={TONE[s]}>{s}</Chip> {RULE_LABEL[r] ?? r} <span className="font-normal">({items.length})</span>
            </h3>
            <ul className="divide-y divide-line rounded-lg border border-line bg-white">
              {items.map((v, i) => {
                const suggestions = v.rule === 'COVERAGE_GAP' && onAddDouble ? parseDoubleSuggestions(v.explanation) : [];
                return (
                  <li key={i} className={`px-3 ${compact ? 'py-1.5 text-xs' : 'py-2 text-sm'}`}>
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="font-medium">{v.subject}</span>
                      {v.windowStart && <span className="text-ink-muted">{fmtDate(v.windowStart, { year: false })}{v.windowEnd && v.windowEnd !== v.windowStart ? ` to ${fmtDate(v.windowEnd, { year: false })}` : ''}</span>}
                      {onShow && v.windowStart && (
                        <button type="button" className="text-navy underline" onClick={() => onShow(v)}>show on schedule</button>
                      )}
                    </div>
                    <p className="mt-0.5 text-ink">{v.explanation}</p>
                    {suggestions.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {suggestions.map((sg) => (
                          <Button key={sg.pilotCode + sg.rotationStart} className="text-xs" onClick={() => onAddDouble?.(sg)} title={`Add DOUBLE for ${sg.pilotCode} on ${sg.tail} from ${fmtDate(sg.rotationStart)}`}>
                            Add DOUBLE: {pilotNames.get(sg.pilotCode) ?? sg.pilotCode} on {sg.tail} from {fmtDate(sg.rotationStart, { year: false })}
                          </Button>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

export function severityCounts(violations: Violation[]): Record<ViolationSeverity, number> {
  const out: Record<ViolationSeverity, number> = { HIGH: 0, WARN: 0, SOFT: 0, INFO: 0 };
  for (const v of violations) out[v.severity] = (out[v.severity] ?? 0) + 1;
  return out;
}
